import { useMutation } from '@apollo/client';
import Colors from '@constants/Colors';
import { DELETE_FILE } from '@gql/file';
import { useDebounce } from '@hooks/useDebounce';
import { useDownloadFile } from '@hooks/useDownloadFile';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { canPreviewFile } from '@utils/fileHelpers';

import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import FileCard from './FileCard';
import FileCardSkeleton from './FileCardSkeleton';
import FileUploadProgress from './FileUploadProgress';
import ScrollArea from './ScrollArea';
import FilePreviewDialog from '@components/InputDocuments/FilePreviewDialog';
import { useAddFilesForTalent, useDownloadAllFilesByTalent, useFileForTalent } from './hooks';


// File size limits
const MAX_VIDEO_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_OTHER_SIZE = 10 * 1024 * 1024; // 10MB

type FileManagerProps = {
    talentId: string;
};
interface UploadingFile {
    id: string;
    name: string;
    progress: number;
    mimetype?: string;
}

const FileManager: React.FC<FileManagerProps> = ({
    talentId,
}) => {
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<{ id: string; name: string } | null>(null);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState<{ _id: string; name: string; url: string } | null>(null);
    const [numPages, setNumPages] = useState<number>(0);

    const { showSnackbar } = useSnackbar();

    const { addFilesForTalent: uploadFiles, filesCountQueryName: uploadFilesCountQueryName } = useAddFilesForTalent();
    const debouncedSearch = useDebounce(searchQuery, 500);

    const isVideoFile = (file: File): boolean => {
        const videoMimeTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-flv'];
        const videoExtensions = ['.mp4', '.mpeg', '.mpg', '.mov', '.avi', '.webm', '.flv', '.mkv'];

        return videoMimeTypes.includes(file.type) ||
            videoExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    };

    const { downloadAllFilesByTalent } = useDownloadAllFilesByTalent();

    const limit = 10;

    const {
        loading,
        fetchMore,
        refetch,
        files,
        hasMore,
        filesQueryName,
        filesCountQueryName,
    } = useFileForTalent(
        talentId,
        'job_application_document',
        limit,
        0,
        debouncedSearch || undefined,
    );

    const [deleteFile, { loading: deleting }] = useMutation(DELETE_FILE, {
        onCompleted: () => {
            showSnackbar('Fichier supprimé avec succès', 'success');
        },
        onError: (error) => {
            showSnackbar(error.message || 'Échec de la suppression du fichier', 'error');
        },
    });

    const filesConnectionKey = filesQueryName === 'GetFilesByTalentUpercut' ? 'getFilesByTalentUpercut' : 'getFilesByTalent';
    const filesCountKey = filesCountQueryName === 'GetFilesCountByTalentsUpercut'
        ? 'getFilesCountByTalentsUpercut'
        : 'getFilesCountByTalents';
    const uploadFilesCountKey = uploadFilesCountQueryName === 'GetFilesCountByTalentsUpercut'
        ? 'getFilesCountByTalentsUpercut'
        : 'getFilesCountByTalents';
    const addFilesMutationResultKey = uploadFilesCountQueryName === 'GetFilesCountByTalentsUpercut'
        ? 'addFilesForTalentUpercut'
        : 'addFilesForTalent';

    const handleLoadMore = useCallback(() => {
        if (!loading && hasMore) {
            fetchMore({
                variables: {
                    offset: files.length,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return previousResult;
                    if (!previousResult?.getFilesByTalent?.files) return fetchMoreResult;

                    return {
                        getFilesByTalent: {
                            ...fetchMoreResult.getFilesByTalent,
                            files: [
                                ...previousResult.getFilesByTalent.files,
                                ...fetchMoreResult.getFilesByTalent.files,
                            ],
                        },
                    };
                },
            });
        }
    }, [loading, hasMore, files.length, fetchMore]);

    const handleDelete = useCallback(
        (fileId: string, fileName: string) => {
            setFileToDelete({ id: fileId, name: fileName });
            setDeleteDialogOpen(true);
        },
        [],
    );

    const handleDeleteConfirm = useCallback(
        async () => {
            if (fileToDelete) {
                await deleteFile({
                    variables: { _id: fileToDelete.id },
                    updateQueries: {
                        [filesQueryName]: (prev, { mutationResult }) => {
                            if (!prev?.[filesConnectionKey]) return prev;
                            const deletedFileId = mutationResult.data.deleteFile._id;
                            return {
                                ...prev,
                                [filesConnectionKey]: {
                                    ...prev[filesConnectionKey],
                                    files: prev[filesConnectionKey].files.filter((file: any) => file._id !== deletedFileId),
                                },
                            };
                        },
                        [filesCountQueryName]: (prev, { mutationResult, queryVariables }) => {
                            if (!prev || !mutationResult.data?.deleteFile) return prev;
                            if (!queryVariables.talentIds?.includes(talentId)) return prev;

                            return {
                                [filesCountKey]: prev[filesCountKey].map(item => {
                                    if (item.talentId === talentId && item.fileCount > 0) {
                                        return {
                                            ...item,
                                            fileCount: item.fileCount - 1
                                        };
                                    }
                                    return item;
                                })
                            };
                        },
                    },
                });
            }
            setDeleteDialogOpen(false);
            setFileToDelete(null);
        },
        [fileToDelete, deleteFile, talentId, filesQueryName, filesCountQueryName, filesConnectionKey, filesCountKey],
    );

    const handleDeleteCancel = useCallback(() => {
        setDeleteDialogOpen(false);
        setFileToDelete(null);
    }, []);

    const handlePreview = useCallback((file: { _id: string; name: string; url: string }) => {
        setPreviewFile(file);
        setPreviewOpen(true);
    }, []);

    const { download: downloadForNavigate, downloadFromUrl } = useDownloadFile({
        onSuccess: url => {
            // Update preview with the actual download URL
            if (previewFile) {
                setPreviewFile(prev => prev ? { ...prev, url } : null);
            }
        },
        onError: error => {
            showSnackbar(error.message || 'Failed to load file', 'error');
        },
    });

    const handleNavigateFile = useCallback((file: { _id: string; name: string; url: string }) => {
        // First update the preview file
        setPreviewFile(file);
        setNumPages(0);
        // Then fetch the actual download URL
        downloadForNavigate(file);
    }, [downloadForNavigate]);

    const handleClosePreview = useCallback(() => {
        setPreviewOpen(false);
        setPreviewFile(null);
        setNumPages(0);
    }, []);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleDownloadFromPreview = useCallback(() => {
        if (previewFile) {
            downloadFromUrl(previewFile.url, previewFile.name);
        }
    }, [previewFile, downloadFromUrl]);

    const handleDownloadAll = useCallback(async () => {
        setDownloadingAll(true);
        try {
            const result = await downloadAllFilesByTalent({
                variables: {
                    talentId,
                    context: 'job_application_document',
                    search: debouncedSearch || undefined,
                },
            });

            const downloadUrl = result?.data?.downloadAllFilesByTalent?.url || result?.data?.downloadAllFilesByTalentUpercut?.url;

            if (downloadUrl) {
                window.open(downloadUrl, '_blank');
                showSnackbar('Téléchargement du fichier ZIP commencé', 'success');
            }
        } catch (error: any) {
            showSnackbar(error.message || 'Échec du téléchargement des fichiers', 'error');
        } finally {
            setDownloadingAll(false);
        }
    }, [talentId, debouncedSearch, downloadAllFilesByTalent, showSnackbar]);

    const handleUploadComplete = useCallback(() => {
        refetch();
    }, [refetch]);


    const processFileUpload = useCallback(
        async (files: File[]) => {
            if (files.length === 0) return;

            // Validate file sizes - moved inside useCallback to avoid dependency issues
            const validateFileSize = (file: File): { valid: boolean; error?: string } => {
                const isVideo = isVideoFile(file);
                const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_OTHER_SIZE;
                const maxSizeMB = isVideo ? 20 : 10;

                if (file.size > maxSize) {
                    return {
                        valid: false,
                        error: `"${file.name}" dépasse la limite de taille (${maxSizeMB}MB pour ${isVideo ? 'les vidéos' : 'les fichiers'})`,
                    };
                }

                return { valid: true };
            };

            const validationResults = files.map(file => ({
                file,
                validation: validateFileSize(file),
            }));

            const invalidFiles = validationResults.filter(result => !result.validation.valid);
            const validFiles = validationResults.filter(result => result.validation.valid).map(result => result.file);

            // Show error messages for invalid files
            invalidFiles.forEach(({ validation }) => {
                if (validation.error) {
                    showSnackbar(validation.error, 'error');
                }
            });

            // If no valid files, return early
            if (validFiles.length === 0) return;

            // Create uploading file placeholders only for valid files
            const newUploadingFiles: UploadingFile[] = validFiles.map((file, index) => ({
                id: `${Date.now()}-${index}`,
                name: file.name,
                progress: 0,
                mimetype: file.type,
            }));
            setUploadingFiles(newUploadingFiles);

            // Upload files one by one
            let successCount = 0;
            let errorCount = 0;

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                const fileId = newUploadingFiles[i].id;

                try {
                    await uploadFiles({
                        variables: {
                            files: [file], // Upload one file at a time
                            talentId,
                            context: 'job_application_document',
                        },
                        context: {
                            fetchOptions: {
                                useUpload: true,
                                onProgress: (progressEvent: ProgressEvent) => {
                                    const percentCompleted = Math.round(
                                        (progressEvent.loaded * 100) / progressEvent.total
                                    );
                                    setUploadingFiles(prev =>
                                        prev.map(f =>
                                            f.id === fileId
                                                ? { ...f, progress: percentCompleted }
                                                : f
                                        )
                                    );
                                },
                                onAbortPossible: (abortHandler: () => void) => {
                                    // Store abort handler if needed for cancellation feature
                                },
                            },
                        },
                        updateQueries: {
                            [uploadFilesCountQueryName]: (prev, { mutationResult, queryVariables }) => {
                                if (!prev || !mutationResult.data?.[addFilesMutationResultKey]?.length) return prev;
                                if (!queryVariables.talentIds?.includes(talentId)) return prev;

                                return {
                                    [uploadFilesCountKey]: prev[uploadFilesCountKey].map(item => {
                                        if (item.talentId === talentId) {
                                            return {
                                                ...item,
                                                fileCount: item.fileCount + mutationResult.data[addFilesMutationResultKey].length
                                            };
                                        }
                                        return item;
                                    })
                                };
                            },
                        },
                    });
                    successCount++;
                } catch (error) {
                    errorCount++;
                    console.error(`Upload error for ${file.name}:`, error);
                    // Mark this file as failed but continue with others
                    setUploadingFiles(prev =>
                        prev.map(f =>
                            f.id === fileId
                                ? { ...f, progress: -1 } // -1 to indicate error
                                : f
                        )
                    );
                }
            }

            // Show completion message
            if (successCount > 0) {
                showSnackbar(
                    `${successCount} fichier${successCount > 1 ? 's' : ''} téléchargé${successCount > 1 ? 's' : ''} avec succès`,
                    'success'
                );
            }
            if (errorCount > 0) {
                showSnackbar(
                    `Échec du téléchargement de ${errorCount} fichier${errorCount > 1 ? 's' : ''}`,
                    'error'
                );
            }

            // Clean up and notify completion
            setTimeout(() => {
                setUploadingFiles([]);
                if (handleUploadComplete) {
                    handleUploadComplete();
                }
            }, 1000); // Brief delay to show 100% progress
        },
        [showSnackbar, uploadFiles, talentId, handleUploadComplete, uploadFilesCountQueryName, uploadFilesCountKey, addFilesMutationResultKey],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: processFileUpload,
        multiple: true,
        noClick: true,
    });

    return (
        <Box
            {...getRootProps()}
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                height: '100%',
                maxHeight: { xs: '100vh', sm: '90vh', md: '80vh' },
            }}>
            <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack spacing={{ xs: 2, sm: 3 }} sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
                    {/* Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: 'Lemon milk pro',
                                    fontSize: { xs: '18px', sm: '22px', md: '25px' },
                                    lineHeight: { xs: '28px', sm: '36px', md: '40px' },
                                    fontWeight: 500,
                                    marginBottom: '4px',
                                }}>
                                Fichiers liés à la candidature
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <i className="fi fi-br-eye" style={{ fontSize: 14 }}></i>

                                <Typography
                                    sx={{
                                        fontFamily: 'Plus Jakarta Sans',
                                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                                        fontWeight: 500,
                                        lineHeight: { xs: '18px', sm: '20px', md: '24px' },
                                    }}>
                                    Seuls les membres de votre groupe peuvent voir les fichiers
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>

                    {/* Search Input with Upload Button */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 1.5, sm: 2 }}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between">
                        <TextField
                            size="small"
                            placeholder="Rechercher par nom de fichier..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <i className="fi fi-br-search" style={{ fontSize: 16, marginRight: 8, color: Colors.NEUTRAL_DARK_GRAY }}></i>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: 'Plus Jakarta Sans',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    borderRadius: '16px',
                                },
                                width: '100%',
                                maxWidth: { xs: '100%', sm: '400px' },
                            }}
                        />
                        <Button
                            component="label"
                            color="secondary"
                            variant="text"
                            startIcon={<i className="fi fi-br-clip" style={{ fontSize: '16px' }}></i>}
                            sx={{
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '13px', sm: '14px' },
                                py: { xs: 1, sm: 0.5 },
                            }}>
                            Ajouter des fichiers
                            <input
                                {...getInputProps()}
                                style={{ display: 'none' }}
                            />
                        </Button>
                    </Stack>
                </Stack>

                {/* Scrollable File List Container */}
                <ScrollArea
                    hasMore={hasMore}
                    loading={loading}
                    onLoadMore={handleLoadMore}
                    sx={{
                        px: { xs: 1.5, sm: 2 },
                        pb: { xs: 1.5, sm: 2 },
                    }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(auto-fill, minmax(140px, 1fr))',
                                sm: 'repeat(auto-fill, minmax(170px, 1fr))',
                                md: 'repeat(auto-fill, minmax(196px, 1fr))',
                            },
                            gap: { xs: '10px', sm: '12px', md: '15px' },
                            width: '100%',
                        }}>

                        {/* Full-page Drag Overlay */}
                        {isDragActive && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 9999,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 2,
                                    pointerEvents: 'none',
                                }}>
                                <Stack spacing={2} alignItems="center">
                                    <i
                                        className="fi fi-br-upload"
                                        style={{
                                            fontSize: '64px',
                                            color: Colors.PRIMARY,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontFamily: 'Plus Jakarta Sans',
                                            fontSize: { xs: '20px', sm: '26px', md: '32px' },
                                            fontWeight: 700,
                                            color: Colors.PRIMARY,
                                        }}>
                                        Déposez ici
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        {/* Upload Progress Placeholders */}
                        {uploadingFiles.length > 0 && (
                            <>
                                {uploadingFiles.map((file) => (
                                    <FileUploadProgress key={file.id} fileName={file.name} progress={file.progress} mimetype={file.mimetype} />
                                ))}
                            </>
                        )}

                        {loading && files.length === 0 ? (
                            <>
                                {Array.from({ length: 5 }).map((_, index) => (
                                    <FileCardSkeleton key={`skeleton-${index}`} />
                                ))}
                            </>
                        ) : (
                            <>
                                {files.map((file: any) => (
                                    <FileCard
                                        key={file._id}
                                        file={file}
                                        onDelete={handleDelete}
                                        onPreview={handlePreview}
                                        deleting={deleting}
                                    />
                                ))}
                            </>
                        )}
                    </Box>
                    {!loading && files.length === 0 && (
                        <Typography
                            align="center"
                            py={{ xs: 3, sm: 4 }}
                            sx={{ fontSize: { xs: '13px', sm: '14px' } }}>
                            {searchQuery ? 'Aucun fichier trouvé.' : 'Aucun fichier disponible.'}
                        </Typography>
                    )}
                </ScrollArea>
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <i className="fi fi-br-file-upload" style={{ fontSize: 14, color: Colors.NEUTRAL_DARK_GRAY }}></i>

                    <Typography
                        sx={{
                            fontFamily: 'Plus Jakarta Sans',
                            fontSize: { xs: '11px', sm: '12px' },
                        }}>
                        Glissez-déposez des fichiers directement ici
                    </Typography>
                </Stack>
                <Button
                    onClick={handleDownloadAll}
                    variant="contained"
                    disabled={downloadingAll || files.length === 0}
                    sx={{
                        fontSize: { xs: '13px', sm: '14px' },
                        width: { xs: '100%', sm: 'auto' }
                    }}
                    startIcon={
                        downloadingAll ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <i className="fi fi-br-download" style={{ fontSize: '16px' }} />
                        )
                    }>
                    {downloadingAll ? 'Préparation...' : 'Télécharger tout'}
                </Button>
            </DialogActions>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        m: { xs: 2, sm: 4 },
                    },
                }}>
                <DialogTitle sx={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: { xs: '18px', sm: '20px' },
                    fontWeight: 700,
                }}>
                    Confirmer la suppression
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: { xs: '13px', sm: '14px' },
                    }}>
                        Êtes-vous sûr de vouloir supprimer "{fileToDelete?.name}" ? Cette action est irréversible.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    gap: { xs: 1, sm: 0 },
                    flexDirection: { xs: 'column', sm: 'row' },
                }}>
                    <Button
                        onClick={handleDeleteCancel}
                        variant="text"
                        color="secondary"
                        // fullWidth={{ xs: true, sm: false }}
                        sx={{
                            fontFamily: 'Plus Jakarta Sans',
                            fontSize: { xs: '13px', sm: '14px' },
                            order: { xs: 2, sm: 1 },
                            width: { xs: '100%', sm: 'auto' },
                        }}>
                        Annuler
                    </Button>
                    <Button
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        sx={{
                            fontFamily: 'Plus Jakarta Sans',
                            fontSize: { xs: '13px', sm: '14px' },
                            order: { xs: 1, sm: 2 },
                            width: { xs: '100%', sm: 'auto' },
                        }}>
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>

            {/* File Preview Dialog */}
            {previewFile && (
                <FilePreviewDialog
                    open={previewOpen}
                    onClose={handleClosePreview}
                    file={{ name: previewFile.name, url: previewFile.url }}
                    onDownload={handleDownloadFromPreview}
                    showDownloadButton={true}
                    numPages={numPages}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                    files={files.filter(f => canPreviewFile(f.mimetype || '', f.name))}
                    currentFileId={previewFile._id}
                    onNavigate={handleNavigateFile}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                    loading={loading}
                />
            )}
        </Box>
    );
};

export default FileManager;
